import { useEffect, useState } from 'react'
import { t } from '@/utils/i18n'
import { useLanguage } from '@/hooks/useLanguage'
import { useDream } from '@/hooks/useDream'
import { usePlan } from '@/hooks/usePlan'
import { useToastContext } from '@/store/contexts/ToastContext'
import { useLoading } from '@/store/contexts/LoadingContext'
import { DreamityPlanResDto, PlanFrequency } from '@/store/types/plan.types'
import Modal from '@/components/common/Modal/Modal'
import Button from '@/components/common/Button/Button'
import { Input } from '@/components/common/Input/Input'

interface Props {
  isOpen: boolean
  onClose: () => void
  plan: DreamityPlanResDto | null
  dreamId: string | null
}

export default function PlanEditModal({ isOpen, onClose, plan, dreamId }: Props) {
  const { language } = useLanguage()
  const { dreams } = useDream()
  const { updatePlan } = usePlan()
  const { showToast } = useToastContext()
  const { startLoading, stopLoading } = useLoading()

  const [form, setForm] = useState({
    planName: '',
    planDesc: '',
    frequency: 'NONE' as PlanFrequency,
    frequencyDay: null as number | null,
    startDate: '',
    endDate: '',
    isCompleted: false,
  })

  useEffect(() => {
    if (plan) {
      setForm({
        planName: plan.planName || '',
        planDesc: plan.planDesc || '',
        frequency: plan.frequency || 'NONE',
        frequencyDay: plan.frequencyDay || null,
        startDate: plan.startDate || '',
        endDate: plan.endDate || '',
        isCompleted: plan.isCompleted || false,
      })
    }
  }, [plan])

  const handleSubmit = async () => {
    if (!plan || !dreamId) return
    try {
      startLoading()
      await updatePlan(dreamId, plan.planId, {
        ...form,
        planName: form.planName.trim(),
        planDesc: form.planDesc.trim(),
      })
      showToast(t('messages.updateSuccess', language), 'success')
      onClose()
    } catch (err) {
      showToast(t('messages.updateFailed', language), 'error')
    } finally {
      stopLoading()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('goalDetail.editPlanTitle', language) || '계획 수정'} size="md">
      <div className="p-4 flex flex-col gap-4">
        <Input label={t('goalDetail.planName', language) || '계획 이름'} value={form.planName} onChange={(e) => setForm({ ...form, planName: e.target.value })} required />
        <Input label={t('goalDetail.planDesc', language) || '계획 설명'} value={form.planDesc} onChange={(e) => setForm({ ...form, planDesc: e.target.value })} placeholder={language === 'ko' ? '계획 설명 (선택)' : 'Plan description (optional)'} />
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">{t('goalDetail.frequency', language) || '빈도'}</label>
            <select
              className="w-full p-2 border border-gray-300 rounded-md bg-white text-sm"
              value={form.frequency}
              onChange={(e) => {
                const newFreq = e.target.value as PlanFrequency
                const selectedDream = dreams.find(d => d.dreamId === dreamId)
                if (newFreq === 'NONE') {
                  setForm({
                    ...form,
                    frequency: newFreq,
                    frequencyDay: null,
                    endDate: form.startDate,
                  })
                } else {
                  setForm({
                    ...form,
                    frequency: newFreq,
                    frequencyDay: null,
                    startDate: selectedDream?.startDate || form.startDate,
                    endDate: selectedDream?.endDate || form.endDate,
                  })
                }
              }}
            >
              <option value="NONE">{t('plan.frequency.none', language) || '없음'}</option>
              <option value="DAILY">{t('plan.frequency.daily', language) || '매일'}</option>
              <option value="WEEKLY">{t('plan.frequency.weekly', language) || '매주'}</option>
              <option value="MONTHLY">{t('plan.frequency.monthly', language) || '매월'}</option>
              <option value="YEARLY">{t('plan.frequency.yearly', language) || '매년'}</option>
            </select>
          </div>

          {form.frequency === 'WEEKLY' && (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">{t('goalDetail.frequencyDay', language) || '요일 선택'}</label>
              <select
                className="w-full p-2 border border-gray-300 rounded-md bg-white text-sm"
                value={form.frequencyDay || ''}
                onChange={(e) => setForm({ ...form, frequencyDay: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">{t('calendar.selectDay', language) || '요일을 선택하세요'}</option>
                <option value="1">{t('calendar.monday', language) || '월요일'}</option>
                <option value="2">{t('calendar.tuesday', language) || '화요일'}</option>
                <option value="3">{t('calendar.wednesday', language) || '수요일'}</option>
                <option value="4">{t('calendar.thursday', language) || '목요일'}</option>
                <option value="5">{t('calendar.friday', language) || '금요일'}</option>
                <option value="6">{t('calendar.saturday', language) || '토요일'}</option>
                <option value="7">{t('calendar.sunday', language) || '일요일'}</option>
              </select>
            </div>
          )}

          {form.frequency === 'MONTHLY' && (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">{t('goalDetail.frequencyDay', language) || (language === 'ko' ? '일 선택' : 'Select day')}</label>
              <select
                className="w-full p-2 border border-gray-300 rounded-md bg-white text-sm"
                value={form.frequencyDay || ''}
                onChange={(e) => setForm({ ...form, frequencyDay: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">{t('calendar.selectDay', language) || (language === 'ko' ? '일을 선택하세요' : 'Select day')}</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>{d}{language === 'ko' ? '일' : ''}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input 
            label={t('goalDetail.startDate', language)} 
            type="date" 
            value={form.startDate} 
            onChange={e => {
              const newStart = e.target.value
              if (form.frequency === 'NONE') {
                setForm({ ...form, startDate: newStart, endDate: newStart })
              } else {
                setForm({ ...form, startDate: newStart })
              }
            }} 
          />
          <Input 
            label={t('goalDetail.endDate', language)} 
            type="date" 
            value={form.endDate} 
            onChange={e => setForm({ ...form, endDate: e.target.value })} 
            readOnly={form.frequency === 'NONE'}
            disabled={form.frequency === 'NONE'}
          />
        </div>



        <Button onClick={handleSubmit}>{t('common.save', language)}</Button>
      </div>
    </Modal>
  )
}
